import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VIEWER_SETTINGS,
  PLATFORM_OPTIONS,
  LANGUAGE_OPTIONS,
  SLIDER_CONFIGS,
  DEFAULT_FILTERS,
} from '../../constants';
import './FilterPanel.css';

/**
 * Filter dropdown panel for stream discovery.
 *
 * Contains multiple filter types:
 * - Viewer settings (checkboxes)
 * - Duration/Views/Donations (sliders with toggle)
 * - Platform selection (checkboxes)
 * - Language dropdown
 * - Tags search
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether panel is visible
 * @param {function} props.onClose - Handler to close panel
 * @param {function} props.onApplyFilters - Handler called with filter state
 * @returns {JSX.Element|null}
 */
function FilterPanel({ isOpen, onClose, onApplyFilters }) {
  const panelRef = useRef(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [tagInput, setTagInput] = useState('');

  /* Close panel on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  /* Handler for viewer settings checkboxes */
  const handleViewerSettingChange = (id) => {
    setFilters((prev) => ({
      ...prev,
      viewerSettings: {
        ...prev.viewerSettings,
        [id]: !prev.viewerSettings[id],
      },
    }));
  };

  /* Handler for slider toggle */
  const handleSliderToggle = (slider) => {
    setFilters((prev) => ({
      ...prev,
      [`${slider}Enabled`]: !prev[`${slider}Enabled`],
    }));
  };

  /* Handler for slider value change */
  const handleSliderChange = (slider, value) => {
    setFilters((prev) => ({
      ...prev,
      [`${slider}Value`]: parseInt(value, 10),
    }));
  };

  /* Handler for platform checkboxes */
  const handlePlatformChange = (platform) => {
    setFilters((prev) => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: !prev.platforms[platform],
      },
    }));
  };

  /* Handler for language selection */
  const handleLanguageChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      language: e.target.value,
    }));
  };

  /* Handler for adding tags */
  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!filters.tags.includes(tagInput.trim())) {
        setFilters((prev) => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput('');
    }
  };

  /* Handler for removing tags */
  const handleRemoveTag = (tagToRemove) => {
    setFilters((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  /* Apply filters */
  const handleViewResults = () => {
    onApplyFilters(filters);
    onClose();
  };

  /* Clear all filters */
  const handleClear = () => {
    setFilters(DEFAULT_FILTERS);
    setTagInput('');
  };

  return (
    <div className="filter-panel" ref={panelRef}>
      {/* Header */}
      <div className="filter-panel__header">
        <h3 className="filter-panel__title">Filter</h3>
        <button
          type="button"
          className="filter-panel__close"
          onClick={onClose}
          aria-label="Close filter panel"
        >
          ×
        </button>
      </div>

      <div className="filter-panel__content">
        {/* Viewer Settings */}
        <section className="filter-panel__section">
          <h4 className="filter-panel__section-title">Viewer Settings</h4>
          {VIEWER_SETTINGS.map((setting) => (
            <label key={setting.id} className="filter-panel__checkbox">
              <input
                type="checkbox"
                checked={filters.viewerSettings[setting.id] || false}
                onChange={() => handleViewerSettingChange(setting.id)}
              />
              <span className="filter-panel__checkbox-mark" />
              <span className="filter-panel__checkbox-label">{setting.label}</span>
            </label>
          ))}
        </section>

        {/* Duration Slider */}
        <SliderFilter
          label="Go Live Duration"
          config={SLIDER_CONFIGS.duration}
          enabled={filters.durationEnabled}
          value={filters.durationValue}
          onToggle={() => handleSliderToggle('duration')}
          onChange={(val) => handleSliderChange('duration', val)}
        />

        {/* Views Slider */}
        <SliderFilter
          label="Views"
          config={SLIDER_CONFIGS.views}
          enabled={filters.viewsEnabled}
          value={filters.viewsValue}
          onToggle={() => handleSliderToggle('views')}
          onChange={(val) => handleSliderChange('views', val)}
        />

        {/* Donations Slider */}
        <SliderFilter
          label="Total Donations"
          config={SLIDER_CONFIGS.donations}
          enabled={filters.donationsEnabled}
          value={filters.donationsValue}
          onToggle={() => handleSliderToggle('donations')}
          onChange={(val) => handleSliderChange('donations', val)}
        />

        {/* Platform Selection */}
        <section className="filter-panel__section">
          <h4 className="filter-panel__section-title">Platform</h4>
          {PLATFORM_OPTIONS.map((platform) => (
            <label key={platform.id} className="filter-panel__checkbox">
              <input
                type="checkbox"
                checked={filters.platforms[platform.id] || false}
                onChange={() => handlePlatformChange(platform.id)}
              />
              <span className="filter-panel__checkbox-mark" />
              <span className="filter-panel__checkbox-label">{platform.label}</span>
            </label>
          ))}
        </section>

        {/* Language Dropdown */}
        <section className="filter-panel__section">
          <h4 className="filter-panel__section-title">Language</h4>
          <select
            className="filter-panel__select"
            value={filters.language}
            onChange={handleLanguageChange}
          >
            {LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </section>

        {/* Tags Search */}
        <section className="filter-panel__section">
          <h4 className="filter-panel__section-title">Search by Tags</h4>
          <input
            type="text"
            className="filter-panel__tag-input"
            placeholder="Type a tag and press Enter"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
          />
          {filters.tags.length > 0 && (
            <div className="filter-panel__tags">
              {filters.tags.map((tag) => (
                <span key={tag} className="filter-panel__tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    aria-label={`Remove ${tag} tag`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Action Buttons */}
      <div className="filter-panel__actions">
        <button
          type="button"
          className="filter-panel__btn filter-panel__btn--primary"
          onClick={handleViewResults}
        >
          View Results
        </button>
        <button
          type="button"
          className="filter-panel__btn filter-panel__btn--secondary"
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/**
 * Slider filter component with toggle and value display.
 */
function SliderFilter({ label, config, enabled, value, onToggle, onChange }) {
  const percentage = ((value - config.min) / (config.max - config.min)) * 100;

  return (
    <section className="filter-panel__section filter-panel__section--slider">
      <div className="filter-panel__slider-header">
        <h4 className="filter-panel__section-title">{label}</h4>
        <label className="filter-panel__toggle">
          <input type="checkbox" checked={enabled} onChange={onToggle} />
          <span className="filter-panel__toggle-mark" />
        </label>
      </div>
      {enabled && (
        <div className="filter-panel__slider-container">
          <div
            className="filter-panel__slider-value"
            style={{ left: `${percentage}%` }}
          >
            {config.formatLabel(value)}
          </div>
          <input
            type="range"
            min={config.min}
            max={config.max}
            step={config.step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="filter-panel__slider"
            style={{
              background: `linear-gradient(to right, var(--primary) ${percentage}%, var(--border-default) ${percentage}%)`,
            }}
          />
          <div className="filter-panel__slider-labels">
            <span>{config.minLabel}</span>
            <span>{config.maxLabel}</span>
          </div>
        </div>
      )}
    </section>
  );
}

SliderFilter.propTypes = {
  label: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  enabled: PropTypes.bool.isRequired,
  value: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

FilterPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
};

export default FilterPanel;
